import { LibrarySymbolInfo } from '@frontends/feature-charting-library/datafeed-api';
import { supportedResolutions } from '@frontends/feature-tv-parts';
import { getSymbolIconUrl } from '@frontends/util-symbol-icons';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { type AppThunk, type RootState } from '../../app/rootState';
import { $axios } from '../../hooks/useAxiosInterceptors';
import { getEntityConfig, TEntityConfig } from '../../hooks/useEntityConfig';

let entityConfig = getEntityConfig();
const SYMBOL_ICONS_BASE_URL = '/assets/icons';

type Account = {
  currency: string;
  login: string;
  balance: number;
};

export interface InitialState {
  accountData: Account | null;
  accountLoaded: boolean;
  loadFailed: boolean;
  symbols: LibrarySymbolInfo[];
  symbolsLoaded: boolean;
}

const initialState: InitialState = {
  symbols: [],
  accountData: null,
  loadFailed: false,
  symbolsLoaded: false,
  accountLoaded: false,
};
export const getSymbols = createAsyncThunk(
  'initialSlice/getSymbols',
  async (
    { accountId, entityConfig: requestEntityConfig }: { accountId: number; entityConfig: TEntityConfig },
    { rejectWithValue },
  ) => {
    try {
      const response = await $axios.get(
        `${requestEntityConfig.VITE_URL_TRADING_API}/api/v1/trading/accounts/${accountId}/symbols`,
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getAccountData = createAsyncThunk(
  'initialSlice/getAccountData',
  async (
    { accountId, entityConfig: requestEntityConfig }: { accountId: number; entityConfig: TEntityConfig },
    { rejectWithValue },
  ) => {
    try {
      const response = await $axios.get(
        `${requestEntityConfig.VITE_URL_TRADING_API}/api/v1/trading/accounts/${accountId}`,
      );

      console.log(response.data);

      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const initFacade =
  (accountId: number): AppThunk<Promise<void>> =>
  async (dispatch) => {
    entityConfig = getEntityConfig();
    await dispatch(getSymbols({ accountId, entityConfig }));
    await dispatch(getAccountData({ accountId, entityConfig }));
  };

export const initialSlice = createSlice({
  name: 'initialSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSymbols.pending, (state) => {
        state.symbolsLoaded = false;
        state.loadFailed = false;
      })
      .addCase(getSymbols.fulfilled, (state, action) => {
        state.symbols = mapperForSymbols(action.payload as RawSymbol[]);
        state.symbolsLoaded = true;
      })
      .addCase(getSymbols.rejected, (state) => {
        state.loadFailed = true;
      })

      .addCase(getAccountData.pending, (state) => {
        state.accountLoaded = false;
      })
      .addCase(getAccountData.fulfilled, (state, action) => {
        state.accountData = action.payload as Account;
        state.accountLoaded = true;
      })
      .addCase(getAccountData.rejected, (state) => {
        state.loadFailed = true;
      });
  },
});

interface RawSymbol {
  calc_mode: number;
  contract_size: number;
  currency_base: string;
  currency_margin: string;
  currency_profit: string;
  description: string;
  digits: number;
  group: string;
  sessions: string;
  symbol: string;
  volume_limit_lots: number;
  volume_max_lots: number;
  volume_min_lots: number;
  volume_step_lots: number;
  watchlist_sessions: string;
}

function mapperForSymbols(symbols: RawSymbol[]): LibrarySymbolInfo[] {
  return symbols.map((rawSymbol) => {
    const logoUrls = getLogoUrls(rawSymbol);

    return {
      name: rawSymbol.symbol,
      ticker: rawSymbol.symbol,
      currency_code: rawSymbol.currency_base,
      original_currency_code: rawSymbol.currency_base,
      visible_plots_set: 'ohlcv',
      delay: 0,
      format: 'price',
      volume_precision: 0,
      description: rawSymbol.description,
      type: rawSymbol.group,
      exchange: entityConfig.VITE_APPLICATION_EXCHANGE,
      timezone: 'Etc/UTC',
      listed_exchange: entityConfig.VITE_APPLICATION_EXCHANGE,
      has_daily: true,
      has_empty_bars: false,
      has_weekly_and_monthly: true,
      has_volume: true,
      supported_resolutions: supportedResolutions,
      data_status: 'streaming',
      session: rawSymbol.watchlist_sessions,
      library_custom_fields: {
        digits: rawSymbol.digits,
        originalSessions: rawSymbol.sessions,
        calc_mode: rawSymbol.calc_mode,
        contract_size: rawSymbol.contract_size,
        currency_base: rawSymbol.currency_base,
        currency_margin: rawSymbol.currency_margin,
        currency_profit: rawSymbol.currency_profit,
        volume_max_lots: rawSymbol.volume_max_lots,
        volume_min_lots: rawSymbol.volume_min_lots,
        volume_step_lots: rawSymbol.volume_step_lots,
        volume_limit_lots: rawSymbol.volume_limit_lots,
      },
      minmov: 1,
      minmov2: 0,
      fractional: false,
      pricescale: Math.pow(10, rawSymbol.digits),
      has_intraday: true,
      has_ticks: true,
      has_seconds: true,
      build_seconds_from_ticks: true,
      logo_urls: logoUrls,
      exchange_logo: '/logo.png',
    };
  });

  function getLogoUrls(symbolObject: RawSymbol): [string] | [string, string] | undefined {
    if (symbolObject.currency_base !== symbolObject.currency_profit) {
      return [
        getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, symbolObject.currency_base),
        getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, symbolObject.currency_profit),
      ];
    } else {
      return [getSymbolIconUrl(SYMBOL_ICONS_BASE_URL, symbolObject.symbol)];
    }
  }
}

export const selectSymbols = (state: RootState): LibrarySymbolInfo[] => state.initialSlice.symbols;
export const selectSymbolsLoaded = (state: RootState): boolean => state.initialSlice.symbolsLoaded;
export const selectAccountLoaded = (state: RootState): boolean => state.initialSlice.accountLoaded;
export const selectInitialLoaded = (state: RootState): boolean =>
  state.initialSlice.symbolsLoaded && state.initialSlice.accountLoaded;
export const selectInitialLoadFailed = (state: RootState): boolean => state.initialSlice.loadFailed;
export const selectSymbolByName = (name: string): ((state: RootState) => LibrarySymbolInfo | null) =>
  createSelector(selectSymbols, (symbols) => symbols.find((symbol) => symbol.name === name) ?? null);

export const selectAccount = (state: RootState): Account | null => state.initialSlice.accountData;

export default initialSlice.reducer;
